import React, { useState, useRef, useEffect, useMemo } from "react";
import { MainLayout } from "./MainLayout";
import { useApp } from "./AppContext";
import { useGetExtra } from "../api/extras/useGetExtra";
import { useUpdateCodeOfConduct } from "../api/extras/useAcceptCodeOfConduct";
import { useCurrentUser } from "../api/auth/useCurrentUser";
import { useTranslation } from "../i18n/TranslationContext";

export function CodeOfConduct() {
	const {
		navigateDirectly,
		navigateToProfileCompletion,
		navigationContext,
		onboardingState,
	} = useApp();
	const { t } = useTranslation();
	const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	// Get current user to fetch their ID
	const { user } = useCurrentUser();

	// Use hooks for fetching and updating code of conduct with dynamic ID
	const { extra } = useGetExtra(user?.extraId);
	const { updateCodeOfConduct, loading: isSubmitting } = useUpdateCodeOfConduct(
		user?.extraId
	);

	// Check if code of conduct is accepted (has timestamp)
	const isCodeOfConductAccepted = useMemo(() => {
		return (
			extra?.code_of_conduct !== null && extra?.code_of_conduct !== undefined
		);
	}, [extra]);

	// Initialize agreements based on whether code of conduct is already accepted
	const [agreements, setAgreements] = useState({
		conduct: false,
		safety: false,
		confidentiality: false,
		uniform: false,
		workingHours: false,
	});

	// Pre-check all checkboxes if code of conduct is already accepted
	useEffect(() => {
		if (isCodeOfConductAccepted) {
			// If accepted, check all boxes and make read-only
			setAgreements({
				conduct: true,
				safety: true,
				confidentiality: true,
				uniform: true,
				workingHours: true,
			});
			setHasScrolledToBottom(true); // Mark as scrolled since user already accepted
		}
	}, [isCodeOfConductAccepted]);

	const handleScroll = () => {
		const container = scrollContainerRef.current;
		if (container) {
			const { scrollTop, scrollHeight, clientHeight } = container;
			const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px tolerance
			setHasScrolledToBottom(isAtBottom);
		}
	};

	useEffect(() => {
		const container = scrollContainerRef.current;
		if (container) {
			container.addEventListener("scroll", handleScroll);
			return () => container.removeEventListener("scroll", handleScroll);
		}
	}, []);

	const handleAgreementChange = (key: keyof typeof agreements) => {
		// Don't allow changes if code of conduct is already accepted
		if (!isCodeOfConductAccepted) {
			setAgreements((prev) => ({ ...prev, [key]: !prev[key] }));
		}
	};

	// For internal users, check all agreements including workingHours
	// For external users, exclude workingHours from validation
	const allAgreed = extra?.internal_extra
		? Object.values(agreements).every(Boolean) && hasScrolledToBottom
		: agreements.conduct &&
		  agreements.safety &&
		  agreements.confidentiality &&
		  agreements.uniform &&
		  hasScrolledToBottom;

	const handleAccept = async () => {
		if (!isCodeOfConductAccepted && allAgreed) {
			// User hasn't accepted yet and all checkboxes are checked
			const success = await updateCodeOfConduct(true);

			if (success) {
				// Navigate back to source screen
				if (navigationContext?.sourceScreen) {
					navigateDirectly(navigationContext.sourceScreen);
				} else {
					navigateToProfileCompletion();
				}
			}
		} else if (isCodeOfConductAccepted) {
			// Already accepted, just navigate
			if (navigationContext?.sourceScreen) {
				navigateDirectly(navigationContext.sourceScreen);
			} else {
				navigateToProfileCompletion();
			}
		}
	};

	const handleDecline = async () => {
		if (!isCodeOfConductAccepted) {
			// User can decline (sets code_of_conduct to null)
			const success = await updateCodeOfConduct(false);

			if (success) {
				// Navigate back to source screen
				if (navigationContext?.sourceScreen) {
					navigateDirectly(navigationContext.sourceScreen);
				} else {
					navigateToProfileCompletion();
				}
			}
		} else {
			// Already accepted, just navigate (shouldn't reach here as button is disabled)
			if (navigationContext?.sourceScreen) {
				navigateDirectly(navigationContext.sourceScreen);
			} else {
				navigateToProfileCompletion();
			}
		}
	};

	return (
		<MainLayout
			showBack={true}
			onBack={() => {
				// If we're in onboarding flow step 2, go back to intro
				// if (onboardingState.currentOnboardingStep === 2) {
				// navigateDirectly("intro-code-of-conduct");
				// } else if (navigationContext?.sourceScreen) {
				navigateDirectly("app-profile-completion-overview");
				// } else {
				// navigateToProfileCompletion();
				// }
			}}
		>
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-3">
					{t("codeOfConduct.title")}
				</h1>
				<p className="text-[var(--ucm-text-medium)]">
					{isCodeOfConductAccepted
						? t("codeOfConduct.accepted")
						: t("codeOfConduct.pleaseRead")}
				</p>
			</div>

			{/* Status Badge */}
			{isCodeOfConductAccepted && (
				<div className="mb-4 px-4 py-2 rounded-lg inline-flex items-center bg-[var(--ucm-light-green)] text-[var(--ucm-dark-green)]">
					<span className="text-sm font-medium">
						{t("codeOfConduct.status")}
					</span>
				</div>
			)}

			{/* Scrollable Content */}
			<div
				ref={scrollContainerRef}
				className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6 max-h-80 overflow-y-auto"
			>
				{extra?.internal_extra ? (
					/* Content for Internal Users */
					<ul className="space-y-4 text-sm text-[var(--ucm-text-dark)] leading-relaxed list-disc pl-5">
						<li>
							<strong>Geltung & Weisungen:</strong> Den Anweisungen des Personals vor Ort ist stets Folge zu leisten.
						</li>
						<li>
							<strong>Professionelles Verhalten:</strong> Respektvoller Umgang mit allen Personen; keine diskriminierende Sprache oder Handlungen.
						</li>
						<li>
							<strong>Zutritt & Ausweise:</strong> Zutritt nur über freigegebene Eingänge und nur in berechtigte Bereiche. Sie müssen sich bei der Bundespolizei ausweisen können; interne Verkehrsregeln gelten.
						</li>
						<li>
							<strong>Sicherheit zuerst:</strong> Alle Sicherheitsprotokolle befolgen, Vorfälle/Gefahren sofort melden. Rauchen, offenes Feuer und eigenmächtige Arbeiten mit Brandgefahr sind verboten.
						</li>
						<li>
							<strong>Alkohol/Drogen:</strong> Das Erscheinen im alkoholisierten oder berauschten Zustand führt automatisch zu einem Ausschluss vom Probebetrieb.
						</li>
						<li>
							<strong>Fahrzeuge & Geräte:</strong> Nur zugelassene/versicherte Fahrzeuge auf Betriebsflächen; Parken nur gekennzeichnet. Bodenabfertigungs-/Terminal-Infrastruktur nur bestimmungsgemäß nutzen.
						</li>
						<li>
							<strong>Gefahrgut & verbotene Gegenstände:</strong> Vorschriften strikt einhalten; unzulässige Gegenstände werden entnommen/entsorgt.
						</li>
						<li>
							<strong>Foto/Film & Werbung:</strong> Nur mit vorheriger Genehmigung; Versammlungen/Promotionen sind anmelde- und genehmigungspflichtig.
						</li>
						<li>
							<strong>Tiere & Drohnen:</strong> Keine Tiere auf Betriebsflächen; Drohnen/UAVs grundsätzlich untersagt.
						</li>
						<li>
							<strong>Sauberkeit & Umwelt:</strong> Abfälle beseitigen; Leckagen/Verunreinigungen sofort melden und aufnehmen; nur normales Abwasser einleiten.
						</li>
						<li>
							<strong>Datenschutz/Video:</strong> Videoüberwachung vorhanden; Daten können bei schwerwiegendem Verstoß gegen den Verhaltenskodex zur Strafverfolgung genutzt werden.
						</li>
						<li>
							<strong>Folgen bei Verstößen:</strong> Verweis vom Gelände, Ausweissperre, Vertragskündigung und ggf. rechtliche Schritte.
						</li>
					</ul>
				) : (
					/* Content for External Users */
					<ul className="space-y-4 text-sm text-[var(--ucm-text-dark)] leading-relaxed list-decimal pl-5">
						<li>
							<strong>Gültigkeit:</strong><br />
							Dieser Verhaltenskodex gilt für alle Teilnehmenden des Terminal 3 Probebetriebs auf dem Flughafengelände Frankfurt am Main.
						</li>
						<li>
							<strong>Professionelles Verhalten:</strong><br />
							Alle Teilnehmenden verpflichten sich zu professionellem und respektvollem Verhalten gegenüber allen Beteiligten.
						</li>
						<li>
							<strong>Zutritt:</strong><br />
							Der Zutritt zum Terminal 3 ist ausschließlich mit gültiger Terminbestätigung und einem gültigem Ausweisdokument mit Foto gestattet. Vor Beginn des Probebetriebs wird das Ausweisdokument mit den Daten der Einladung abgeglichen. Teilnehmende dürfen sich nur in den Bereichen aufhalten, die im Rahmen ihrer jeweiligen Rolle für den Probebetriebstag vorgesehen sind und ihnen am Morgen des Probebetriebs zugeteilt werden.
						</li>
						<li>
							<strong>Sicherheit geht vor:</strong><br />
							Die Einhaltung aller Sicherheitsvorschriften hat oberste Priorität.
						</li>
						<li>
							<strong>Alkohol und Drogen:</strong><br />
							Der Konsum von Alkohol und Drogen ist während des Probebetriebs strengstens untersagt.
						</li>
						<li>
							<strong>Anreise:</strong><br />
							Bei Anreise mit dem Auto ist das Parken ausschließlich auf den für Statisten des Probebetriebs ausgewiesenen Flächen im Parkhaus P60 gestattet. Eine Erstattung von Parkgebühren bei Nutzung anderer Parkflächen ist ausgeschlossen. Für Anreisen mit öffentlichen Verkehrsmitteln erfolgt keine Erstattung der Ticketkosten.
						</li>
						<li>
							<strong>Mitnahme von Gegenständen:</strong><br />
							Teilnehmenden ist es untersagt, eigene größere Taschen oder kofferähnliche Gegenstände zum Probebetrieb mitzubringen. Fraport behält sich das Recht vor, mitgebrachte Gegenstände beim Zutritt zum Terminal 3 zu kontrollieren. Gefährliche oder sicherheitsrelevante Gegenstände können dabei entfernt oder die gesamte Tasche einbehalten werden.
							Das Mitführen verbotener Gegenstände ist untersagt. Für den Zugang zu den Sicherheitsbereichen gelten zudem dieselben Sicherheitsvorschriften wie an regulären Flughafenkontrollen. Gefährliche oder verbotene Gegenstände werden konfisziert.
						</li>
						<li>
							<strong>Foto- und Videoaufnahmen:</strong><br />
							Für Statisten gilt während des Probebetriebs ein striktes Film- und Fotoverbot. Fraport und ucm behalten sich das Recht vor, Statisten für interne und externe Kommunikationszwecke zu filmen und zu fotografieren. Zudem kann die Teilnahme von Pressevertretern erfolgen, wodurch Statisten möglicherweise in Fernseh- oder sonstige Medienformate aufgenommen werden.
						</li>
						<li>
							<strong>Tiere:</strong><br />
							Das Mitführen von Tieren ist während des Probebetriebs im Terminal 3 nicht gestattet. Dies gilt auch für Assistenz- oder Blindenführhunde sowie andere Service-Tiere.
						</li>
						<li>
							<strong>Sauberkeit und Umwelt:</strong><br />
							Alle Teilnehmenden werden gebeten, zur Sauberkeit im Terminal beizutragen und ihren Müll in den dafür vorgesehenen Mülleimern zu entsorgen.
						</li>
						<li>
							<strong>Datenschutz:</strong><br />
							Alle personenbezogenen Daten werden gemäß den geltenden Datenschutzbestimmungen behandelt.
							Die Teilnehmenden stimmen zu, dass ihre angegebenen personenbezogenen Daten im Vorfeld der Teilnahme überprüft werden. Diese Daten können dabei auch an zuständige Behörden zur Durchführung luftsicherheitsrechtlicher Überprüfungen übermittelt werden.
							Im Terminal 3 besteht eine großflächige Kameraüberwachung, die auch während des Probebetriebs aktiv ist. Mit ihrer Teilnahme erklären sich die Teilnehmenden ausdrücklich mit der Aufzeichnung und Verarbeitung der dabei entstehenden Bilddaten einverstanden.
						</li>
						<li>
							<strong>Konsequenzen:</strong><br />
							Verstöße gegen diesen Verhaltenskodex können zum Ausschluss vom Probebetrieb führen.
						</li>
						<li>
							<strong>Maßnahmen der Bundespolizei:</strong><br />
							Im Rahmen des Probebetriebes werden unter anderem Prozessstellen der Bundespolizei durchlaufen. Hierbei werden die Teilnehmenden grundsätzlich einer realen Kontrolle durch die Behörde unterzogen, welche unter anderem die Feststellung der Identität sowie die Erhebung und den Abgleich personenbezogener Daten, unter anderem mit polizeilichen Datenbanken, umfassen kann. Die Bundespolizeidirektion Flughafen Frankfurt am Main behält sich bei Erforderlichkeit die Durchführung weiterer polizeilicher Maßnahmen vor. Mit Ihrer Anmeldung zur Teilnahme erklären Sie sich mit den Maßnahmen der Bundespolizei einverstanden und stimmen diesen ausdrücklich zu.
						</li>
					</ul>
				)}
			</div>

			{/* Scroll Indicator */}
			{!hasScrolledToBottom && (
				<div className="text-center mb-4">
					<p className="text-sm text-[var(--ucm-warning-orange)]">
						{isCodeOfConductAccepted
							? t("codeOfConduct.scrollToBottom")
							: t("codeOfConduct.scrollToContinue")}
					</p>
				</div>
			)}

			{/* Agreement Checkboxes */}
			<div className="space-y-4 mb-6">
				<div className="flex items-center space-x-3">
					<input
						type="checkbox"
						id="conduct"
						checked={agreements.conduct}
						onChange={() => handleAgreementChange("conduct")}
						disabled={!hasScrolledToBottom || isCodeOfConductAccepted}
						className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)] flex-shrink-0"
					/>
					<label
						htmlFor="conduct"
						className={`text-sm leading-5 ${hasScrolledToBottom ? "text-[var(--ucm-text-dark)]" : "text-[var(--ucm-text-light)]"}`}
					>
						{t("codeOfConduct.agreements.conduct")}
					</label>
				</div>

				<div className="flex items-center space-x-3">
					<input
						type="checkbox"
						id="safety"
						checked={agreements.safety}
						onChange={() => handleAgreementChange("safety")}
						disabled={!hasScrolledToBottom || isCodeOfConductAccepted}
						className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)] flex-shrink-0"
					/>
					<label
						htmlFor="safety"
						className={`text-sm leading-5 ${hasScrolledToBottom ? "text-[var(--ucm-text-dark)]" : "text-[var(--ucm-text-light)]"}`}
					>
						{t("codeOfConduct.agreements.safety")}
					</label>
				</div>

				<div className="flex items-center space-x-3">
					<input
						type="checkbox"
						id="confidentiality"
						checked={agreements.confidentiality}
						onChange={() => handleAgreementChange("confidentiality")}
						disabled={!hasScrolledToBottom || isCodeOfConductAccepted}
						className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)] flex-shrink-0"
					/>
					<label
						htmlFor="confidentiality"
						className={`text-sm leading-5 ${hasScrolledToBottom ? "text-[var(--ucm-text-dark)]" : "text-[var(--ucm-text-light)]"}`}
					>
						{t("codeOfConduct.agreements.confidentiality")}
					</label>
				</div>

				<div className="flex items-center space-x-3">
					<input
						type="checkbox"
						id="uniform"
						checked={agreements.uniform}
						onChange={() => handleAgreementChange("uniform")}
						disabled={!hasScrolledToBottom || isCodeOfConductAccepted}
						className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)] flex-shrink-0"
					/>
					<label
						htmlFor="uniform"
						className={`text-sm leading-5 ${hasScrolledToBottom ? "text-[var(--ucm-text-dark)]" : "text-[var(--ucm-text-light)]"}`}
					>
						{t("codeOfConduct.agreements.uniform")}
					</label>
				</div>

				{/* Working Hours checkbox - only for internal users */}
				{extra?.internal_extra && (
					<div className="flex items-center space-x-3">
						<input
							type="checkbox"
							id="workingHours"
							checked={agreements.workingHours}
							onChange={() => handleAgreementChange("workingHours")}
							disabled={!hasScrolledToBottom || isCodeOfConductAccepted}
							className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)] flex-shrink-0"
						/>
						<label
							htmlFor="workingHours"
							className={`text-sm leading-5 ${hasScrolledToBottom ? "text-[var(--ucm-text-dark)]" : "text-[var(--ucm-text-light)]"}`}
						>
							{t("codeOfConduct.agreements.workingHours")}
						</label>
					</div>
				)}
			</div>

			{/* Action Buttons */}
			<div className="space-y-3">
				<button
					onClick={handleAccept}
					disabled={(!allAgreed && !isCodeOfConductAccepted) || isSubmitting}
					className={`w-full h-12 font-semibold rounded-full transition-colors duration-200 ${
						isCodeOfConductAccepted
							? "bg-[var(--ucm-success-green)] text-white hover:bg-[var(--ucm-dark-green)]"
							: allAgreed && !isSubmitting
								? "bg-[var(--ucm-success-green)] text-white hover:bg-[var(--ucm-dark-green)]"
								: "bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-medium)] cursor-not-allowed"
					}`}
				>
					{isSubmitting
						? t("codeOfConduct.processing")
						: isCodeOfConductAccepted
							? t("common.continue")
							: t("codeOfConduct.accept")}
				</button>

				{/* Back Button */}
				<button
					onClick={() => navigateDirectly("app-profile-completion-overview")}
					className="w-full h-12 bg-white border-2 border-[var(--ucm-border-medium)] text-[var(--ucm-text-dark)] font-medium rounded-full hover:bg-gray-50 transition-colors duration-200"
				>
					{t("common.back")}
				</button>

				{/* <button
					onClick={handleDecline}
					disabled={isCodeOfConductAccepted || isSubmitting}
					className={`w-full h-12 bg-white border-2 font-medium rounded-full transition-colors duration-200 ${
						isCodeOfConductAccepted
							? "border-[var(--ucm-border-light)] text-[var(--ucm-text-light)] cursor-not-allowed"
							: "border-[var(--ucm-error-red)] text-[var(--ucm-error-red)] hover:bg-red-50"
					}`}
				>
					{isCodeOfConductAccepted
						? t("codeOfConduct.alreadyAccepted")
						: t("codeOfConduct.decline")}
				</button> */}
			</div>
		</MainLayout>
	);
}
